import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, TextField, Button,
    Stepper, Step, StepLabel, CircularProgress, Alert,
    Stack, MenuItem, Fade, Paper, Grid
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    NavigateNext as NextIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as BackIcon,
    AssignmentInd as ProfileIcon,
    ContactMail as ContactsIcon,
    AccountBalance as BankIcon,
    Info as InfoIcon
} from '@mui/icons-material';

const NewHirePortal = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const tenant = searchParams.get('tenant') || localStorage.getItem('tenant') || '';

    // Flow State
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [verificationError, setVerificationError] = useState(null);
    const [verificationWarnings, setVerificationWarnings] = useState([]);
    const [enableAiParser, setEnableAiParser] = useState(true);
    const [parsing, setParsing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    // Document Buffers
    const [cvFile, setCvFile] = useState(null);
    const [nrcFrontFile, setNrcFrontFile] = useState(null);
    const [nrcBackFile, setNrcBackFile] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);

    // Candidate Profile State
    const [formData, setFormData] = useState({
        full_name: '',
        gender: 'MALE',
        dob: '',
        nrc_number: '',
        phone: '',
        email: '',
        address: '',
        bank_name: '',
        bank_account: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relation: '',
        marital_status: 'SINGLE',
        blood_group: '',
        passport_number: '',
        social_security_no: ''
    });

    const steps = enableAiParser
        ? ['Upload Documents', 'Verify Profile Details', 'Additional Information']
        : ['Manual Information Input', 'Additional Information'];

    // Verify token and load configurations on startup
    useEffect(() => {
        if (!token) {
            setVerificationError('Onboarding security token is missing from the URL.');
            setLoading(false);
            return;
        }

        const verifyLink = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL1}/onboarding/portal/info`, {
                    params: { token },
                    headers: { 'X-Tenant': tenant }
                });

                if (res.data.success) {
                    setEnableAiParser(res.data.enableAiParser);
                    const cand = res.data.candidate;
                    setFormData(prev => ({
                        ...prev,
                        full_name: cand.full_name || '',
                        email: cand.email || ''
                    }));

                    // If they previously filled some details
                    if (cand.submitted_data) {
                        setFormData(cand.submitted_data);
                    }
                }
            } catch (err) {
                setVerificationError(err.response?.data?.message || 'Invalid or expired onboarding session link.');
            } finally {
                setLoading(false);
            }
        };

        verifyLink();
    }, [token, tenant]);

    // Parse uploaded CV and/or NRC Front scans at once when clicking Next
    const handleVerifyAndNext = async () => {
        if (!cvFile && !nrcFrontFile) {
            nextStep();
            return;
        }

        setParsing(true);
        setError(null);
        setVerificationWarnings([]);

        try {
            let cvData = null;
            let nrcData = null;

            // 1. If CV is uploaded, parse it
            if (cvFile) {
                const cvForm = new FormData();
                cvForm.append('token', token);
                cvForm.append('resume', cvFile);
                const cvRes = await axios.post(`${process.env.REACT_APP_API_URL1}/onboarding/portal/parse-resume`, cvForm, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'X-Tenant': tenant
                    }
                });
                if (cvRes.data.success) {
                    cvData = cvRes.data.data;
                }
            }

            // 2. If NRC Front is uploaded, parse it
            if (nrcFrontFile) {
                const nrcForm = new FormData();
                nrcForm.append('token', token);
                nrcForm.append('resume', nrcFrontFile);
                const nrcRes = await axios.post(`${process.env.REACT_APP_API_URL1}/onboarding/portal/parse-resume`, nrcForm, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'X-Tenant': tenant
                    }
                });
                if (nrcRes.data.success) {
                    nrcData = nrcRes.data.data;
                }
            }

            const mergedData = {};
            const fieldsToMerge = [
                'full_name', 'nrc_number', 'dob', 'gender', 'phone', 'address',
                'marital_status', 'blood_group', 'passport_number', 'social_security_no',
                'bank_name', 'bank_account_no', 'emergency_contact_name',
                'emergency_contact_phone', 'emergency_contact_relation'
            ];

            fieldsToMerge.forEach(field => {
                let val = null;
                if (nrcData && nrcData[field] !== undefined && nrcData[field] !== null && nrcData[field] !== '') {
                    val = nrcData[field];
                } else if (cvData && cvData[field] !== undefined && cvData[field] !== null && cvData[field] !== '') {
                    val = cvData[field];
                }
                if (val !== null) {
                    mergedData[field] = val;
                }
            });

            // Cross check comparison warnings
            const warnings = [];
            if (cvData && nrcData) {
                const normStr = (s) => s?.toLowerCase().replace(/\s+/g, '') || '';
                const cvName = normStr(cvData.full_name);
                const nrcName = normStr(nrcData.full_name);
                if (cvName && nrcName && cvName !== nrcName) {
                    warnings.push(`Name discrepancy: CV shows "${cvData.full_name}", but ID Card shows "${nrcData.full_name}".`);
                }

                const cvDob = cvData.dob ? cvData.dob.trim() : '';
                const nrcDob = nrcData.dob ? nrcData.dob.trim() : '';
                if (cvDob && nrcDob && cvDob !== nrcDob) {
                    warnings.push(`Date of Birth discrepancy: CV shows "${cvData.dob}", but ID Card shows "${nrcData.dob}".`);
                }

                const normNrc = (s) => s?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
                const cvNrcNum = normNrc(cvData.nrc_number);
                const nrcNrcNum = normNrc(nrcData.nrc_number);
                if (cvNrcNum && nrcNrcNum && cvNrcNum !== nrcNrcNum) {
                    warnings.push(`NRC Number discrepancy: CV shows "${cvData.nrc_number}", but ID Card shows "${nrcData.nrc_number}".`);
                }
            }
            setVerificationWarnings(warnings);

            // Normalize bank name if parsed
            let normalizedBankName = mergedData.bank_name;
            if (normalizedBankName) {
                const upperBank = normalizedBankName.toUpperCase().trim();
                const matchedBank = ["KBZ", "AYA", "CB", "UAB", "A Bank", "MAB", "YOMA", "MTB", "FPB", "FDB", "AGD", "MCB"].find(b => 
                    upperBank.includes(b.toUpperCase()) || b.toUpperCase().includes(upperBank)
                );
                if (matchedBank) {
                    normalizedBankName = matchedBank;
                }
            }

            // Update form state with parsed values
            setFormData(prev => ({
                ...prev,
                full_name: mergedData.full_name || prev.full_name,
                nrc_number: mergedData.nrc_number || prev.nrc_number,
                dob: mergedData.dob || prev.dob,
                gender: mergedData.gender ? mergedData.gender.toUpperCase() : prev.gender,
                phone: mergedData.phone || prev.phone,
                address: mergedData.address || prev.address,
                marital_status: mergedData.marital_status ? mergedData.marital_status.toUpperCase() : prev.marital_status,
                blood_group: mergedData.blood_group || prev.blood_group,
                passport_number: mergedData.passport_number || prev.passport_number,
                social_security_no: mergedData.social_security_no || prev.social_security_no,
                bank_name: normalizedBankName || prev.bank_name,
                bank_account: mergedData.bank_account_no || prev.bank_account,
                emergency_contact_name: mergedData.emergency_contact_name || prev.emergency_contact_name,
                emergency_contact_phone: mergedData.emergency_contact_phone || prev.emergency_contact_phone,
                emergency_contact_relation: mergedData.emergency_contact_relation || prev.emergency_contact_relation
            }));

            nextStep();
        } catch (err) {
            console.error('File parsing failed:', err);
            setError(err.response?.data?.message || 'AI extraction failed for some files. Please verify and fill details manually.');
            // Go to manual verify step anyway so they can input details manually
            nextStep();
        } finally {
            setParsing(false);
        }
    };

    const handleInputChange = (field, val) => {
        setFormData(prev => ({ ...prev, [field]: val }));
    };

    const nextStep = () => {
        setActiveStep(prev => prev + 1);
    };

    const prevStep = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleFormSubmit = async () => {
        setSubmitting(true);
        setError(null);

        const submitForm = new FormData();
        submitForm.append('token', token);
        submitForm.append('submitted_data', JSON.stringify(formData));

        if (photoFile) submitForm.append('profile_photo', photoFile);
        if (nrcFrontFile) submitForm.append('id_front', nrcFrontFile);
        if (nrcBackFile) submitForm.append('id_back', nrcBackFile);
        if (cvFile) submitForm.append('cv_file', cvFile);

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/onboarding/portal/submit`, submitForm, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-Tenant': tenant
                }
            });

            if (res.data.success) {
                setSuccess(true);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit onboarding profile.');
        } finally {
            setSubmitting(false);
        }
    };

    // Render loading indicator
    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f8fafc' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size={50} sx={{ color: 'primary.main' }} />
                    <Typography color="#64748b">Verifying secure credentials...</Typography>
                </Stack>
            </Box>
        );
    }

    // Render success view
    if (success) {
        return (
            <Box sx={{
                minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f5f9 100%)', px: 2
            }}>
                <Fade in duration={800}>
                    <Paper elevation={0} sx={{
                        maxWidth: 500, p: 5, textAlign: 'center', borderRadius: 6,
                        bgcolor: '#ffffff', border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                            All Set!
                        </Typography>
                        <Typography color="#64748b" sx={{ mb: 4, lineHeight: 1.6 }}>
                            Thank you, <strong>{formData.full_name}</strong>! Your onboarding details and credentials have been submitted. HR will review your documents and activate your official employee profile shortly.
                        </Typography>
                        <Typography variant="caption" color="#94a3b8" display="block">
                            You can now close this tab. Welcome aboard!
                        </Typography>
                    </Paper>
                </Fade>
            </Box>
        );
    }

    // Render parsing loading view
    if (parsing) {
        return (
            <Box sx={{
                minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f5f9 100%)', px: 2
            }}>
                <Fade in duration={800}>
                    <Paper elevation={0} sx={{
                        maxWidth: 500, p: 5, textAlign: 'center', borderRadius: 6,
                        bgcolor: '#ffffff', border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
                    }}>
                        <CircularProgress size={60} sx={{ color: 'primary.main', mb: 4 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                            AI Document Parsing
                        </Typography>
                        <Typography color="#64748b" sx={{ mb: 4, lineHeight: 1.6 }}>
                            Our AI system is extracting details from your CV and NRC front scan to prefill the registration forms. This will only take a moment.
                        </Typography>
                    </Paper>
                </Fade>
            </Box>
        );
    }

    // Render link verification error view (submitted, expired, or invalid)
    if (verificationError) {
        return (
            <Box sx={{
                minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
                background: 'linear-gradient(135deg, #ffebee 0%, #f1f5f9 100%)', px: 2
            }}>
                <Fade in duration={800}>
                    <Paper elevation={0} sx={{
                        maxWidth: 500, p: 5, textAlign: 'center', borderRadius: 6,
                        bgcolor: '#ffffff', border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
                    }}>
                        <InfoIcon sx={{ fontSize: 80, color: '#3b82f6', mb: 3 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                            Link Status
                        </Typography>
                        <Typography color="#64748b" sx={{ mb: 4, lineHeight: 1.6 }}>
                            {verificationError}
                        </Typography>
                        <Typography variant="caption" color="#94a3b8" display="block">
                            Please contact HR if you have any questions.
                        </Typography>
                    </Paper>
                </Fade>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f5f9 100%)',
            py: 6, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
            {/* Onboarding Card */}
            <Card sx={{
                width: '100%', maxWidth: 750, borderRadius: 6,
                bgcolor: '#ffffff', border: '1px solid #e2e8f0',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.04)'
            }}>
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>

                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', mb: 1 }}>
                            Onboarding Portal
                        </Typography>
                        <Typography variant="body2" color="#64748b">
                            Complete your registration details for <strong>{tenant.toUpperCase()}</strong>
                        </Typography>
                    </Box>

                    {/* Stepper */}
                    <Stepper activeStep={activeStep} alternativeLabel sx={{
                        mb: 5,
                        '& .MuiStepLabel-label': { color: '#cbd5e1', fontWeight: 600 },
                        '& .MuiStepLabel-label.Mui-active': { color: 'primary.main' },
                        '& .MuiStepLabel-label.Mui-completed': { color: '#10b981' },
                        '& .MuiStepIcon-root': { color: '#cbd5e1' },
                        '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
                        '& .MuiStepIcon-root.Mui-completed': { color: '#10b981' }
                    }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {error && (
                        <Alert severity="error" sx={{ mb: 4, borderRadius: 3, bgcolor: '#fef2f2', color: '#dc2626', border: '1px solid #fee2e2' }}>
                            {error}
                        </Alert>
                    )}

                    {/* STEP 1: Upload (Only if AI parsing is enabled) */}
                    {enableAiParser && activeStep === 0 && (
                        <Fade in timeout={400}>
                            <Box>
                                <Typography variant="h6" color="#0f172a" sx={{ fontWeight: 700, mb: 1 }}>
                                    Upload CV or NRC Card
                                </Typography>
                                <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>
                                    Upload your resume (PDF, Word) or NRC Card scan (JPEG/PNG). Our AI system will extract your personal details and prefill the form automatically.
                                </Typography>

                                <Stack spacing={3}>
                                    {/* Dropzone for CV */}
                                    <Box sx={{
                                        border: '2px dashed #cbd5e1',
                                        borderRadius: 4, p: 4, textAlign: 'center', cursor: 'pointer',
                                        transition: 'all 0.3s', bgcolor: '#f8fafc',
                                        '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f3ff' }
                                    }} component="label">
                                        <input type="file" hidden accept=".pdf,.doc,.docx" onChange={(e) => {
                                            const f = e.target.files[0];
                                            if (f) setCvFile(f);
                                        }} />
                                        <Stack spacing={1} alignItems="center">
                                            <UploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                            <Typography color="#1e293b" fontWeight={600}>
                                                {cvFile ? cvFile.name : 'Upload your CV / Resume'}
                                            </Typography>
                                            {cvFile && (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setCvFile(null);
                                                    }}
                                                    sx={{ mt: 1, textTransform: 'none' }}
                                                >
                                                    Remove CV
                                                </Button>
                                            )}
                                            <Typography variant="caption" color="#64748b">
                                                PDF only, Max size 10MB
                                            </Typography>
                                        </Stack>
                                    </Box>

                                    {/* Grid for NRC Card Uploads */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{
                                                border: '1px dashed #e2e8f0',
                                                borderRadius: 4, p: 3, textAlign: 'center', cursor: 'pointer',
                                                bgcolor: '#f8fafc', '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f3ff' }
                                            }} component="label">
                                                <input type="file" hidden accept="image/*" onChange={(e) => {
                                                    const f = e.target.files[0];
                                                    if (f) setNrcFrontFile(f);
                                                }} />
                                                <UploadIcon sx={{ fontSize: 24, color: '#64748b', mb: 1 }} />
                                                <Typography variant="body2" color="#1e293b" fontWeight={600}>
                                                    {nrcFrontFile ? nrcFrontFile.name : 'NRC Card Front Scan'}
                                                </Typography>
                                                {nrcFrontFile && (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setNrcFrontFile(null);
                                                        }}
                                                        sx={{ mt: 0.5, textTransform: 'none' }}
                                                    >
                                                        Remove Front
                                                    </Button>
                                                )}
                                                <Typography variant="caption" color="#64748b" display="block">
                                                    JPEG/PNG Image
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{
                                                border: '1px dashed #e2e8f0',
                                                borderRadius: 4, p: 3, textAlign: 'center', cursor: 'pointer',
                                                bgcolor: '#f8fafc', '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f3ff' }
                                            }} component="label">
                                                <input type="file" hidden accept="image/*" onChange={(e) => {
                                                    const f = e.target.files[0];
                                                    if (f) setNrcBackFile(f);
                                                }} />
                                                <UploadIcon sx={{ fontSize: 24, color: '#64748b', mb: 1 }} />
                                                <Typography variant="body2" color="#1e293b" fontWeight={600}>
                                                    {nrcBackFile ? nrcBackFile.name : 'NRC Card Back Scan'}
                                                </Typography>
                                                {nrcBackFile && (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setNrcBackFile(null);
                                                        }}
                                                        sx={{ mt: 0.5, textTransform: 'none' }}
                                                    >
                                                        Remove Back
                                                    </Button>
                                                )}
                                                <Typography variant="caption" color="#64748b" display="block">
                                                    JPEG/PNG Image
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Stack>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                                    <Button
                                        variant="contained"
                                        endIcon={<NextIcon />}
                                        onClick={handleVerifyAndNext}
                                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 2.5, px: 4, py: 1.2, fontWeight: 700 }}
                                    >
                                        {(cvFile || nrcFrontFile) ? 'Analyze & Next' : 'Next'}
                                    </Button>
                                </Box>
                            </Box>
                        </Fade>
                    )}

                    {/* STEP 2: Verify Profile Details */}
                    {((enableAiParser && activeStep === 1) || (!enableAiParser && activeStep === 0)) && (
                        <Fade in timeout={400}>
                            <Box>
                                <Typography variant="h6" color="#0f172a" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ProfileIcon sx={{ color: 'primary.main' }} /> Verify Profile details
                                </Typography>
                                <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>
                                    Make sure all fields match your ID card. Fill in any missing details.
                                </Typography>

                                {verificationWarnings.length > 0 && (
                                    <Alert severity="warning" sx={{ mb: 4, borderRadius: 3, bgcolor: '#fffbeb', color: '#b45309', border: '1px solid #fef3c7' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                                            Verification Warning: Discrepancies detected between your CV and official ID scan. Please verify and prioritize your official government ID details.
                                        </Typography>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            {verificationWarnings.map((w, idx) => (
                                                <li key={idx} style={{ fontSize: 13, fontWeight: 500 }}>{w}</li>
                                            ))}
                                        </ul>
                                    </Alert>
                                )}

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Full Name" value={formData.full_name}
                                            onChange={(e) => handleInputChange('full_name', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth select label="Gender" value={formData.gender}
                                            onChange={(e) => handleInputChange('gender', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        >
                                            <MenuItem value="MALE">Male</MenuItem>
                                            <MenuItem value="FEMALE">Female</MenuItem>
                                            <MenuItem value="OTHER">Other</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Date of Birth" type="date" value={formData.dob}
                                            onChange={(e) => handleInputChange('dob', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="NRC Card Number" value={formData.nrc_number}
                                            placeholder="e.g. 12/LaMaTa(N)123456"
                                            onChange={(e) => handleInputChange('nrc_number', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Phone Number" value={formData.phone}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Work Email" value={formData.email} disabled
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth select label="Marital Status" value={formData.marital_status}
                                            onChange={(e) => handleInputChange('marital_status', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        >
                                            <MenuItem value="SINGLE">Single</MenuItem>
                                            <MenuItem value="MARRIED">Married</MenuItem>
                                            <MenuItem value="DIVORCED">Divorced</MenuItem>
                                            <MenuItem value="WIDOWED">Widowed</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Blood Type" value={formData.blood_group} placeholder="e.g. A, B, AB, O"
                                            onChange={(e) => handleInputChange('blood_group', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Passport Number" value={formData.passport_number} placeholder="Optional"
                                            onChange={(e) => handleInputChange('passport_number', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Social Security No (SSB)" value={formData.social_security_no} placeholder="Optional"
                                            onChange={(e) => handleInputChange('social_security_no', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth label="Current Address" value={formData.address} multiline rows={2}
                                            onChange={(e) => handleInputChange('address', e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        />
                                    </Grid>
                                </Grid>

                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
                                    {enableAiParser && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<BackIcon />}
                                            onClick={prevStep}
                                            sx={{ color: '#64748b', borderColor: '#cbd5e1', borderRadius: 2.5, px: 3, '&:hover': { bgcolor: '#f1f5f9' } }}
                                        >
                                            Back
                                        </Button>
                                    )}
                                    <Button
                                        variant="contained"
                                        endIcon={<NextIcon />}
                                        onClick={nextStep}
                                        disabled={!formData.full_name || !formData.nrc_number || !formData.phone}
                                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 2.5, px: 4, py: 1.2, ml: 'auto', fontWeight: 700 }}
                                    >
                                        Verify & Next
                                    </Button>
                                </Stack>
                            </Box>
                        </Fade>
                    )}

                    {/* STEP 3: Additional Information (Bank, Photos, Emergency Contact) */}
                    {((enableAiParser && activeStep === 2) || (!enableAiParser && activeStep === 1)) && (
                        <Fade in timeout={400}>
                            <Box>
                                <Typography variant="h6" color="#0f172a" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BankIcon sx={{ color: 'primary.main' }} /> Additional Details
                                </Typography>
                                <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>
                                    Input your payroll details, emergency contact, and upload your final profile photo.
                                </Typography>

                                <Stack spacing={3}>
                                    {/* Bank Info */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={
                                            !["KBZ", "AYA", "CB", "UAB", "A Bank", "MAB", "YOMA", "MTB", "FPB", "FDB", "AGD", "MCB", ""].includes(formData.bank_name) ? 3.5 : 6
                                        }>
                                            <TextField
                                                fullWidth select label="Bank Name" 
                                                value={["KBZ", "AYA", "CB", "UAB", "A Bank", "MAB", "YOMA", "MTB", "FPB", "FDB", "AGD", "MCB"].includes(formData.bank_name) ? formData.bank_name : (formData.bank_name === '' ? '' : 'Other')}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === 'Other') {
                                                        handleInputChange('bank_name', 'Other ');
                                                    } else {
                                                        handleInputChange('bank_name', val);
                                                    }
                                                }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                            >
                                                <MenuItem value="">Select Bank</MenuItem>
                                                {["KBZ", "AYA", "CB", "UAB", "A Bank", "MAB", "YOMA", "MTB", "FPB", "FDB", "AGD", "MCB"].map(b => (
                                                    <MenuItem key={b} value={b}>{b}</MenuItem>
                                                ))}
                                                <MenuItem value="Other">Other</MenuItem>
                                            </TextField>
                                        </Grid>
                                        {!["KBZ", "AYA", "CB", "UAB", "A Bank", "MAB", "YOMA", "MTB", "FPB", "FDB", "AGD", "MCB", ""].includes(formData.bank_name) && (
                                            <Grid item xs={12} sm={2.5}>
                                                <TextField
                                                    fullWidth label="Specify Bank" 
                                                    value={formData.bank_name.startsWith('Other ') ? '' : formData.bank_name}
                                                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth label="Bank Account Number" value={formData.bank_account}
                                                onChange={(e) => handleInputChange('bank_account', e.target.value)}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Emergency Contact */}
                                    <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 700, mt: 1 }}>
                                        <ContactsIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Emergency Contact
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth label="Contact Name" value={formData.emergency_contact_name}
                                                onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={
                                            !['Mother', 'Father', 'Spouse', 'Brother', 'Sister', 'Son', 'Daughter', 'Sibling', 'Guardian', 'Friend', ''].includes(formData.emergency_contact_relation) ? 2.5 : 4
                                        }>
                                            <TextField
                                                fullWidth select label="Relationship"
                                                value={['Mother', 'Father', 'Spouse', 'Brother', 'Sister', 'Son', 'Daughter', 'Sibling', 'Guardian', 'Friend'].includes(formData.emergency_contact_relation) ? formData.emergency_contact_relation : (formData.emergency_contact_relation === '' ? '' : 'Other')}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === 'Other') {
                                                        handleInputChange('emergency_contact_relation', 'Other ');
                                                    } else {
                                                        handleInputChange('emergency_contact_relation', val);
                                                    }
                                                }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                            >
                                                <MenuItem value="">Select Relationship</MenuItem>
                                                {['Mother', 'Father', 'Spouse', 'Brother', 'Sister', 'Son', 'Daughter', 'Sibling', 'Guardian', 'Friend'].map(r => (
                                                    <MenuItem key={r} value={r}>{r}</MenuItem>
                                                ))}
                                                <MenuItem value="Other">Other</MenuItem>
                                            </TextField>
                                        </Grid>
                                        {!['Mother', 'Father', 'Spouse', 'Brother', 'Sister', 'Son', 'Daughter', 'Sibling', 'Guardian', 'Friend', ''].includes(formData.emergency_contact_relation) && (
                                            <Grid item xs={12} sm={1.5}>
                                                <TextField
                                                    fullWidth label="Specify"
                                                    value={formData.emergency_contact_relation.startsWith('Other ') ? '' : formData.emergency_contact_relation}
                                                    onChange={(e) => handleInputChange('emergency_contact_relation', e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth label="Contact Phone" value={formData.emergency_contact_phone}
                                                onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Profile Photo upload */}
                                    <Typography variant="subtitle2" color="#0f172a" sx={{ fontWeight: 700 }}>
                                        Profile Photo
                                    </Typography>
                                    <Box sx={{
                                        border: '1px dashed #cbd5e1', borderRadius: 3, p: 2,
                                        textAlign: 'center', bgcolor: '#f8fafc', cursor: 'pointer',
                                        '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f3ff' }
                                    }} component="label">
                                        <input type="file" hidden accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0])} />
                                        <Typography variant="body2" color="#1e293b" fontWeight={600}>
                                            {photoFile ? `Selected: ${photoFile.name}` : 'Upload Profile Photo (White Background Preferred)'}
                                        </Typography>
                                    </Box>

                                    {/* Manual mode document uploads */}
                                    {!enableAiParser && (
                                        <>
                                            <Typography variant="subtitle2" color="#0f172a" sx={{ fontWeight: 700 }}>
                                                CV & ID Documents
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={4}>
                                                    <Button variant="outlined" component="label" fullWidth sx={{ color: '#475569', borderColor: '#cbd5e1', py: 1.5, borderRadius: 3, '&:hover': { bgcolor: '#f1f5f9' }, textTransform: 'none' }}>
                                                        {cvFile ? `CV: ${cvFile.name.slice(0, 10)}...` : 'Upload CV (PDF/Word)'}
                                                        <input type="file" hidden accept=".pdf,.doc,.docx" onChange={(e) => setCvFile(e.target.files[0])} />
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Button variant="outlined" component="label" fullWidth sx={{ color: '#475569', borderColor: '#cbd5e1', py: 1.5, borderRadius: 3, '&:hover': { bgcolor: '#f1f5f9' }, textTransform: 'none' }}>
                                                        {nrcFrontFile ? `Front: ${nrcFrontFile.name.slice(0, 8)}...` : 'NRC Front'}
                                                        <input type="file" hidden accept="image/*" onChange={(e) => setNrcFrontFile(e.target.files[0])} />
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Button variant="outlined" component="label" fullWidth sx={{ color: '#475569', borderColor: '#cbd5e1', py: 1.5, borderRadius: 3, '&:hover': { bgcolor: '#f1f5f9' }, textTransform: 'none' }}>
                                                        {nrcBackFile ? `Back: ${nrcBackFile.name.slice(0, 8)}...` : 'NRC Back'}
                                                        <input type="file" hidden accept="image/*" onChange={(e) => setNrcBackFile(e.target.files[0])} />
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 5 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<BackIcon />}
                                        onClick={prevStep}
                                        disabled={submitting}
                                        sx={{ color: '#64748b', borderColor: '#cbd5e1', borderRadius: 2.5, px: 3, '&:hover': { bgcolor: '#f1f5f9' } }}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        variant="contained"
                                        endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                        onClick={handleFormSubmit}
                                        disabled={submitting || !formData.bank_name || !formData.bank_account || !formData.emergency_contact_name || !formData.emergency_contact_phone}
                                        sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 2.5, px: 4, py: 1.2, fontWeight: 700 }}
                                    >
                                        {submitting ? 'Submitting...' : 'Submit Profile'}
                                    </Button>
                                </Stack>
                            </Box>
                        </Fade>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default NewHirePortal;
